import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Crown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TranscriptionPanel } from '@/components/subtitles';
import { useRecordings, Recording } from '@/hooks/useRecordings';
import { useSubscription } from '@/hooks/useSubscription';

interface TranscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  recording: Recording | null;
}

/**
 * 字幕轉錄 Dialog
 * Pro 專屬功能
 */
export function TranscriptionDialog({
  isOpen,
  onClose,
  recording,
}: TranscriptionDialogProps) {
  const { t } = useTranslation();
  const { currentPlan } = useSubscription();
  const { getRecordingUrl } = useRecordings();
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);

  const isPro = currentPlan === 'pro';

  // Load audio blob when dialog opens
  useEffect(() => {
    if (!isOpen || !recording || !isPro) {
      setAudioBlob(null);
      return;
    }

    let cancelled = false;

    const loadAudio = async () => {
      setLoading(true);
      try {
        const url = await getRecordingUrl(recording.storage_path);
        if (url && !cancelled) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Audio fetch failed: ${response.status}`);
          }
          const blob = await response.blob();
          if (!cancelled) {
            setAudioBlob(blob);
          }
        }
      } catch (error) {
        console.error('Failed to load audio:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAudio();

    return () => {
      cancelled = true;
    };
  }, [isOpen, recording, getRecordingUrl]);

  const handleClose = useCallback(() => {
    setAudioBlob(null);
    onClose();
  }, [onClose]);

  if (!recording) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto border-2 border-black rounded-xl bg-[#F5F0E8] shadow-[8px_8px_0_#1A1A1A] p-6 focus:outline-none">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="px-3 py-1 bg-[#FFD93D] border-2 border-black rounded-lg">
              {t('transcription.dialog.title', '字幕生成')}
            </div>
            <span className="text-sm font-medium text-black/60 truncate max-w-[300px] mt-1">
              {recording.filename}
            </span>
          </DialogTitle>
        </DialogHeader>

        {!isPro ? (
          // Pro upgrade prompt
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="w-16 h-16 rounded-full bg-[#FFD93D] border-2 border-black flex items-center justify-center">
              <Crown className="w-8 h-8 text-black" />
            </div>
            <h3 className="text-lg font-bold">
              {t('transcription.promo.title', '升級至 Pro 解鎖字幕功能')}
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              {t('transcription.promo.description', '自動將您的錄音轉換為字幕，支援中英日韓粵語，可編輯並匯出 SRT/VTT 格式。')}
            </p>
            <button
              onClick={() => window.location.href = '/#pricing'}
              className="
                px-6 py-3 bg-[#FF6B9D] text-black font-bold
                border-2 border-black rounded-xl
                hover:translate-x-0.5 hover:translate-y-0.5
                hover:shadow-[4px_4px_0_#1A1A1A]
                active:translate-x-1 active:translate-y-1 active:shadow-none
                transition-all duration-100
              "
            >
              {t('transcription.promo.upgrade', '查看方案')}
            </button>
          </div>
        ) : loading ? (
          // Loading state
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-600">{t('transcription.loading', '載入音頻中...')}</p>
          </div>
        ) : (
          // Transcription panel
          <TranscriptionPanel
            audioBlob={audioBlob || undefined}
            recordingId={recording.id}
            scriptId={recording.script_id || undefined}
            filename={recording.filename.replace(/\.[^/.]+$/, '')}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
